import { useEffect, useMemo, useState, type Key, type ReactElement, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Spinner, Tabs } from '@heroui/react';
import { Segment } from '@heroui-pro/react';
import { trpc } from '@/lib/trpc';
import { PlanFeatureCompare, type FeatureConfigItem } from './PlanFeatureCompare';

type PlanSku = {
  id?: string | null;
  code?: string | null;
  name?: string | null;
  saleType?: string | null;
  billingPeriodUnit?: string | null;
  tier?: number | null;
  metadata?: unknown;
  skuPriceRule?: Array<{ unitPrice?: number | string | null }> | null;
};

type PlanCard = {
  key: string;
  members: PlanSku[];
  displaySku: PlanSku;
};

/** HeroUI Pro Segment.Item 当前类型未正确暴露 children，做运行时兼容包装 */
const BillingSegmentItem = Segment.Item as unknown as (props: {
  id: string;
  children?: ReactNode;
}) => ReactElement;

const SCOPE_LABEL: Record<string, string> = {
  PERSONAL: '个人',
  TEAM: '团队',
  ENTERPRISE: '企业',
  ALL: '不限',
};

const SCOPE_ORDER = Object.keys(SCOPE_LABEL);

function scopeOrderIndex(scope: string): number {
  const index = SCOPE_ORDER.indexOf(scope);
  return index === -1 ? SCOPE_ORDER.length : index;
}

const PERIOD_LABEL: Record<string, string> = {
  MONTH: '月付',
  YEAR: '年付',
};

const PERIOD_PRICE_LABEL: Record<string, string> = {
  MONTH: '月',
  YEAR: '年',
};

function formatYuan(unitPrice: number | string | null | undefined): number {
  if (unitPrice === undefined || unitPrice === null) return 0;
  const fen = typeof unitPrice === 'string' ? Number(unitPrice) : unitPrice;
  if (!Number.isFinite(fen)) return 0;
  return Math.round(fen) / 100;
}

function skuPriceRule(sku: PlanSku) {
  return sku.skuPriceRule?.[0] ?? null;
}

function isFreeSku(sku: PlanSku): boolean {
  return Number(skuPriceRule(sku)?.unitPrice ?? 0) <= 0 || Boolean(sku.code?.includes('FREE'));
}

function readFeatureConfig(value: unknown): FeatureConfigItem[] | null {
  if (!value || typeof value !== 'object') return null;
  const config = (value as { featureConfig?: unknown }).featureConfig;
  return Array.isArray(config) ? (config as FeatureConfigItem[]) : null;
}

function skuGroupKey(sku: PlanSku): string {
  if (sku.tier != null) return `tier:${sku.tier}`;
  return sku.code ?? String(sku.id ?? '');
}

function pickDisplaySku(members: PlanSku[], billingPeriodUnit: string): PlanSku {
  return (
    members.find(
      (sku) => sku.saleType === 'SUBSCRIPTION' && sku.billingPeriodUnit === billingPeriodUnit
    ) ??
    members.find((sku) => sku.saleType === 'SUBSCRIPTION') ??
    members.find((sku) => sku.saleType === 'ONE_TIME') ??
    members[0]
  );
}

function buildPlanCards(skus: PlanSku[], billingPeriodUnit: string): PlanCard[] {
  const groups = new Map<string, PlanSku[]>();
  for (const sku of skus) {
    const key = skuGroupKey(sku);
    const list = groups.get(key) ?? [];
    list.push(sku);
    groups.set(key, list);
  }

  const cards: PlanCard[] = [];
  for (const [key, members] of groups) {
    const displaySku = pickDisplaySku(members, billingPeriodUnit);
    if (!displaySku) continue;
    cards.push({ key, members, displaySku });
  }

  return cards.sort(
    (a, b) =>
      Number(a.displaySku.tier ?? 99) - Number(b.displaySku.tier ?? 99) ||
      (a.displaySku.code ?? '').localeCompare(b.displaySku.code ?? '')
  );
}

export default function ProductsPage() {
  const navigate = useNavigate();
  const [planScope, setPlanScope] = useState('');
  const [billingPeriodUnit, setBillingPeriodUnit] = useState('YEAR');

  const { data: products = [], isLoading, isError, error } = trpc.bizProduct.getByType.useQuery({
    type: 'PLAN',
  });

  const scopes = useMemo(() => {
    const seen = new Set<string>();
    for (const product of products) {
      const scope = product.targetScope;
      if (scope) seen.add(scope);
    }
    return [...seen].sort((a, b) => scopeOrderIndex(a) - scopeOrderIndex(b));
  }, [products]);

  useEffect(() => {
    if (scopes.length > 0 && !scopes.includes(planScope)) {
      setPlanScope(scopes[0]);
    }
  }, [scopes, planScope]);

  const currentProduct = useMemo(
    () => products.find((product) => product.targetScope === planScope) ?? null,
    [products, planScope]
  );

  const skus = (currentProduct?.skus ?? []) as PlanSku[];

  const periodUnits = useMemo(() => {
    const seen = new Set<string>();
    const list: string[] = [];
    for (const sku of skus) {
      if (sku.saleType !== 'SUBSCRIPTION') continue;
      const unit = sku.billingPeriodUnit;
      if (unit !== 'MONTH' && unit !== 'YEAR') continue;
      if (seen.has(unit)) continue;
      seen.add(unit);
      list.push(unit);
    }
    return list;
  }, [skus]);

  useEffect(() => {
    if (periodUnits.length === 0) return;
    if (!periodUnits.includes(billingPeriodUnit)) {
      setBillingPeriodUnit(periodUnits.includes('YEAR') ? 'YEAR' : periodUnits[0]);
    }
  }, [periodUnits, billingPeriodUnit]);

  const planCards = useMemo(
    () => buildPlanCards(skus, billingPeriodUnit),
    [skus, billingPeriodUnit]
  );

  const handlePlanChange = (key: Key) => {
    setPlanScope(String(key));
  };

  const handleBillingChange = (key: Key) => {
    setBillingPeriodUnit(String(key));
  };

  const renderCards = () => {
    if (planCards.length === 0) {
      return (
        <div className="flex min-h-[12rem] items-center justify-center">
          <p className="text-sm text-default-500">暂无该类型套餐</p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
        {planCards.map((card) => {
          const sku = card.displaySku;
          const unitPrice = formatYuan(skuPriceRule(sku)?.unitPrice);
          const free = isFreeSku(sku);
          const priceUnit =
            sku.saleType === 'SUBSCRIPTION'
              ? (PERIOD_PRICE_LABEL[billingPeriodUnit] ?? billingPeriodUnit)
              : null;

          return (
            <Card
              key={card.key}
              className="border border-default-200 bg-content1 shadow-none"
            >
              <Card.Header className="flex flex-col items-start gap-2">
                <Card.Title className="text-2xl font-bold text-foreground">
                  {sku.name}
                </Card.Title>
              </Card.Header>

              <Card.Content>
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-sm text-default-500">
                    {sku.saleType === 'SUBSCRIPTION' ? '订阅价格' : '价格'}
                  </span>
                  <span className="text-right text-base text-foreground">
                    <span className="text-red-500 mr-2">¥</span>
                    <span className="font-bold text-2xl mr-2 text-red-500">{unitPrice}</span>
                    <span className="ml-1 text-default-500">
                      {sku.saleType === 'SUBSCRIPTION' ? `席位/${priceUnit}` : '席位'}
                    </span>
                  </span>
                </div>
              </Card.Content>

              <Card.Footer>
                {free || sku.saleType === 'ONE_TIME' ? (
                  <Button
                    variant="primary"
                    className="w-full"
                    size="lg"
                    onPress={() =>
                      navigate(`/register?space-type=${encodeURIComponent(planScope)}`)
                    }
                  >
                    免费试用
                  </Button>
                ) : (
                  <Button variant="primary" className="w-full" size="lg" onPress={() => {}}>
                    咨询
                  </Button>
                )}
              </Card.Footer>
            </Card>
          );
        })}
      </div>
    );
  };

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
      {isLoading ? (
        <div className="flex min-h-[40vh] items-center justify-center">
          <Spinner className="size-8" />
        </div>
      ) : isError ? (
        <div className="flex min-h-[40vh] flex-col items-center justify-center gap-2">
          <p className="text-danger">加载失败</p>
          <p className="text-sm text-default-500">{error?.message || '未知错误'}</p>
        </div>
      ) : (
        <Tabs
          selectedKey={planScope}
          onSelectionChange={handlePlanChange}
          className="flex w-full flex-col gap-10"
        >
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6">
            <Tabs.ListContainer>
              <Tabs.List aria-label="套餐类型">
                {scopes.map((scope) => (
                  <Tabs.Tab key={scope} id={scope}>
                    {SCOPE_LABEL[scope] ?? scope}
                    <Tabs.Indicator />
                  </Tabs.Tab>
                ))}
              </Tabs.List>
            </Tabs.ListContainer>

            {periodUnits.length > 0 ? (
              <Segment selectedKey={billingPeriodUnit} onSelectionChange={handleBillingChange}>
                {periodUnits.map((unit) => (
                  <BillingSegmentItem key={unit} id={unit}>
                    <Segment.Separator />
                    {PERIOD_LABEL[unit] ?? unit}
                  </BillingSegmentItem>
                ))}
              </Segment>
            ) : null}
          </div>

          {scopes.map((scope) => (
            <Tabs.Panel key={scope} id={scope}>
              {scope === planScope ? renderCards() : null}
            </Tabs.Panel>
          ))}
        </Tabs>
      )}

      {!isLoading && !isError ? (
        <PlanFeatureCompare
          products={planCards.map((card) => ({
            id: String(card.displaySku.id),
            name: card.displaySku.name,
            featureConfig: readFeatureConfig(card.displaySku.metadata),
          }))}
        />
      ) : null}
    </div>
  );
}
